import { useState } from "react";
import { ClipboardPaste, Wand2 } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(16).slice(2, 10);
const fromHM = (s) => { const [h, m] = String(s).trim().split(":").map(Number); return (h || 0) * 60 + (m || 0); };

export const reverseRoute = (r) => {
  const total = r.stops[r.stops.length - 1]?.offset_min ?? 0;
  const maxKm = r.stops[r.stops.length - 1]?.km ?? 0;
  const ids = Object.fromEntries(r.stops.map((s) => [s.id, uid()]));
  const parts = r.name.split("→").map((x) => x.trim()).reverse();
  return {
    name: parts.length > 1 ? parts.join(" → ") : `${r.name} (vuelta)`,
    stops: [...r.stops].reverse().map((s) => ({ ...s, id: ids[s.id], offset_min: total - s.offset_min, km: maxKm - s.km })),
    fares: r.fares.map((f) => ({ ...f, from_id: ids[f.to_id], to_id: ids[f.from_id] })),
  };
};

const PasteStops = ({ route, setRoute }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const apply = () => {
    const stops = text.split(/\r?\n/).map((l) => l.split(/\t|;/).map((x) => x.trim())).filter((c) => c[0])
      .map(([name, city, hm, km]) => ({ id: uid(), name, city: city || name.split(" · ")[0], offset_min: fromHM(hm || "0:00"), km: Number(String(km || 0).replace(/\./g, "").replace(",", ".")) || 0 }));
    if (stops.length < 2) return toast.error("Pegá al menos 2 paradas, una por línea.");
    setRoute({ ...route, stops, fares: [] });
    setOpen(false);
    toast.success(`${stops.length} paradas cargadas. Ahora completá las tarifas.`);
  };
  return (
    <div className="rounded-2xl bg-[var(--paper)] p-4">
      <button type="button" onClick={() => setOpen(!open)} data-testid="stops-paste-toggle" className="inline-flex items-center gap-2 text-sm font-bold"><ClipboardPaste className="h-4 w-4" /> Pegar paradas desde Excel</button>
      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-[var(--muted-ink)]">Copiá 4 columnas de tu planilla, una parada por fila: <b>Parada · Ciudad · Tiempo desde la salida (hh:mm) · Km</b>. Reemplaza las paradas actuales y borra las tarifas.</p>
          <textarea data-testid="stops-paste-input" value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder={"Retiro · Terminal\tBuenos Aires\t00:00\t0\nRosario · Terminal\tRosario\t04:00\t300"} className="w-full rounded-xl border border-black/10 p-3 font-mono text-xs" />
          <button type="button" onClick={apply} data-testid="stops-paste-apply" className="rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-bold text-white">Cargar paradas</button>
        </div>
      )}
    </div>
  );
};

const AutoFares = ({ route, setRoute, cats }) => {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState({ cat: cats[0], perKm: 60, min: 15000, round: 500, overwrite: false });
  const apply = () => {
    const st = route.stops;
    const keep = route.fares.filter((f) => f.category !== cfg.cat || !cfg.overwrite);
    const has = new Set(keep.filter((f) => f.category === cfg.cat).map((f) => `${f.from_id}|${f.to_id}`));
    const add = [];
    st.forEach((a, i) => st.slice(i + 1).forEach((b) => {
      if (a.city === b.city || has.has(`${a.id}|${b.id}`)) return;
      const raw = Math.max(cfg.min, (b.km - a.km) * cfg.perKm);
      add.push({ from_id: a.id, to_id: b.id, category: cfg.cat, price: Math.round(raw / (cfg.round || 1)) * (cfg.round || 1) });
    }));
    setRoute({ ...route, fares: [...keep, ...add] });
    toast.success(`${add.length} tarifas ${cfg.cat} calculadas. Revisalas en la matriz.`);
  };
  const num = (k) => ({ type: "number", value: cfg[k], onChange: (e) => setCfg({ ...cfg, [k]: Number(e.target.value) }), className: "h-9 w-24 rounded-lg border border-black/10 px-2" });
  return (
    <div className="rounded-2xl bg-[var(--paper)] p-4">
      <button type="button" onClick={() => setOpen(!open)} data-testid="fares-auto-toggle" className="inline-flex items-center gap-2 text-sm font-bold"><Wand2 className="h-4 w-4" /> Calcular tarifas por kilómetro</button>
      {open && (
        <div className="mt-3 flex flex-wrap items-end gap-3 text-xs">
          <label>Categoría<select data-testid="fares-auto-cat" value={cfg.cat} onChange={(e) => setCfg({ ...cfg, cat: e.target.value })} className="mt-1 block h-9 rounded-lg border border-black/10 px-2">{cats.map((c) => <option key={c}>{c}</option>)}</select></label>
          <label>$ por km<input data-testid="fares-auto-perkm" {...num("perKm")} /></label>
          <label>Mínimo $<input data-testid="fares-auto-min" {...num("min")} /></label>
          <label>Redondear a $<input {...num("round")} /></label>
          <label className="flex items-center gap-2 pb-2"><input type="checkbox" checked={cfg.overwrite} onChange={(e) => setCfg({ ...cfg, overwrite: e.target.checked })} /> Reemplazar las ya cargadas</label>
          <button type="button" onClick={apply} data-testid="fares-auto-apply" className="h-9 rounded-full bg-[var(--ink)] px-4 font-bold text-white">Calcular</button>
        </div>
      )}
    </div>
  );
};

export const RouteTools = (props) => (
  <div className="grid gap-3 md:grid-cols-2">
    <PasteStops {...props} />
    <AutoFares {...props} />
  </div>
);
